import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useWallet } from '../contexts/WalletContext';
import { normalizeWallet, fetchFavoritePieces, toggleFavoritePiece, isPieceFavorited } from '../lib/favorites';

export const useSupabaseFavorites = (skipEventReload = false) => {
  const { isConnected, publicKey } = useWallet();
  const [favoritePieces, setFavoritePieces] = useState(new Set());
  const [favoriteCapsules, setFavoriteCapsules] = useState(new Set());
  const [favoriteWallets, setFavoriteWallets] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get user wallet address (always lowercase)
  const getUserWallet = () => {
    return publicKey?.toString().toLowerCase() || 'anonymous';
  };

  // Load favorites from Supabase
  const loadFavorites = async () => {
    if (!isConnected || !publicKey) {
      setFavoritePieces(new Set());
      setFavoriteCapsules(new Set());
      setFavoriteWallets(new Set());
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const userWallet = getUserWallet();
      
      // Get favorite pieces using the correct query pattern
      const { data: pieceFavorites, error: pieceError } = await supabase
        .from('favorites')
        .select('item_id')
        .eq('user_wallet', userWallet)
        .eq('item_type', 'piece')
        .order('created_at', { ascending: false });

      if (pieceError) {
        throw pieceError;
      }

      const pieces = new Set(pieceFavorites?.map(fav => fav.item_id) || []);
      
      // For capsules and wallets, use the same pattern
      const { data: otherFavorites, error: fetchError } = await supabase
        .from('favorites')
        .select('item_type, item_id')
        .eq('user_wallet', userWallet)
        .in('item_type', ['capsule', 'wallet']);

      if (fetchError) {
        throw fetchError;
      }

      // Group other favorites by type
      const capsules = new Set();
      const wallets = new Set();

      otherFavorites?.forEach(fav => {
        switch (fav.item_type) {
          case 'capsule':
            capsules.add(fav.item_id);
            break;
          case 'wallet':
            wallets.add(fav.item_id);
            break;
        }
      });

      setFavoritePieces(pieces);
      setFavoriteCapsules(capsules);
      setFavoriteWallets(wallets);

    } catch (err) {
      console.error('Error loading favorites:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load favorites when wallet connects/disconnects
  useEffect(() => {
    loadFavorites();
  }, [isConnected, publicKey]);

  // Listen for favorite changes to sync across components (unless disabled)
  useEffect(() => {
    if (skipEventReload) {
      console.log('⏭️ useSupabaseFavorites: Event reload disabled');
      return;
    }

    const handleFavoritesChange = () => {
      console.log('🔄 useSupabaseFavorites: Favorites changed, reloading...');
      loadFavorites();
    };

    window.addEventListener('pieceFavoritesChanged', handleFavoritesChange);
    window.addEventListener('capsuleFavoritesChanged', handleFavoritesChange);
    
    return () => {
      window.removeEventListener('pieceFavoritesChanged', handleFavoritesChange);
      window.removeEventListener('capsuleFavoritesChanged', handleFavoritesChange);
    };
  }, [isConnected, publicKey, skipEventReload]);

  // Toggle favorite status for pieces
  const toggleFavorite = async (pieceId) => {
    console.log('toggleFavorite called with pieceId:', pieceId);
    
    if (!isConnected || !publicKey) {
      console.log('Not connected or no public key');
      console.warn('Please connect your wallet to favorite pieces');
      return false;
    }

    try {
      const userWallet = getUserWallet();
      const isCurrentlyFavorited = favoritePieces.has(pieceId);
      
      console.log('Toggle favorite - Wallet:', userWallet, 'PieceId:', pieceId, 'Currently favorited:', isCurrentlyFavorited);

      if (isCurrentlyFavorited) {
        // Remove from favorites
        console.log('Removing from favorites...');
        const { error } = await supabase
          .from('favorites')
          .delete()
          .eq('user_wallet', userWallet)
          .eq('item_type', 'piece')
          .eq('item_id', pieceId);

        if (error) {
          console.error('Error removing favorite:', error);
          throw error;
        }
        console.log('Successfully removed from favorites');

        setFavoritePieces(prev => {
          const newSet = new Set(prev);
          newSet.delete(pieceId);
          return newSet;
        });
      } else {
        // Add to favorites
        console.log('Adding to favorites...');
        const { error } = await supabase
          .from('favorites')
          .insert({
            user_wallet: userWallet,
            item_type: 'piece',
            item_id: pieceId
          });

        if (error) {
          // Handle duplicate key error - item is already favorited
          if (error.code === '23505') {
            console.log('⚠️ Item already favorited in database, updating local state');
            setFavoritePieces(prev => new Set([...prev, pieceId]));
          } else {
            console.error('Error adding favorite:', error);
            throw error;
          }
        } else {
          console.log('Successfully added to favorites');
          setFavoritePieces(prev => new Set([...prev, pieceId]));
        }
      }

      // Notify all components that piece favorites changed
      window.dispatchEvent(new Event('pieceFavoritesChanged'));
      console.log('📢 Dispatched pieceFavoritesChanged event');

      return true;
    } catch (err) {
      console.error('Error toggling favorite:', err);
      setError(err.message);
      return false;
    }
  };

  // Toggle favorite status for capsules
  const toggleCapsuleFavorite = async (capsuleId) => {
    if (!isConnected || !publicKey) {
      console.warn('Please connect your wallet to favorite capsules');
      return false;
    }

    try {
      const userWallet = getUserWallet();
      const isCurrentlyFavorited = favoriteCapsules.has(capsuleId);

      if (isCurrentlyFavorited) {
        // Remove from favorites
        const { error } = await supabase
          .from('favorites')
          .delete()
          .eq('user_wallet', userWallet)
          .eq('item_type', 'capsule')
          .eq('item_id', capsuleId);

        if (error) throw error;

        setFavoriteCapsules(prev => {
          const newSet = new Set(prev);
          newSet.delete(capsuleId);
          return newSet;
        });
      } else {
        // Add to favorites
        const { error } = await supabase
          .from('favorites')
          .insert({
            user_wallet: userWallet,
            item_type: 'capsule',
            item_id: capsuleId
          });

        if (error) throw error;

        setFavoriteCapsules(prev => new Set([...prev, capsuleId]));
      }

      // Notify all components that capsule favorites changed
      window.dispatchEvent(new Event('capsuleFavoritesChanged'));
      console.log('📢 Dispatched capsuleFavoritesChanged event');

      return true;
    } catch (err) {
      console.error('Error toggling capsule favorite:', err);
      setError(err.message);
      return false;
    }
  };

  // Toggle favorite status for wallets
  const toggleWalletFavorite = async (walletId) => {
    if (!isConnected || !publicKey) {
      console.warn('Please connect your wallet to favorite other wallets');
      return false;
    }

    try {
      const userWallet = getUserWallet();
      const isCurrentlyFavorited = favoriteWallets.has(walletId);

      if (isCurrentlyFavorited) {
        // Remove from favorites
        const { error } = await supabase
          .from('favorites')
          .delete()
          .eq('user_wallet', userWallet)
          .eq('item_type', 'wallet')
          .eq('item_id', walletId);

        if (error) throw error;

        setFavoriteWallets(prev => {
          const newSet = new Set(prev);
          newSet.delete(walletId);
          return newSet;
        });
      } else {
        // Add to favorites
        const { error } = await supabase
          .from('favorites')
          .insert({
            user_wallet: userWallet,
            item_type: 'wallet',
            item_id: walletId
          });

        if (error) throw error;

        setFavoriteWallets(prev => new Set([...prev, walletId]));
      }

      return true;
    } catch (err) {
      console.error('Error toggling wallet favorite:', err);
      setError(err.message);
      return false;
    }
  };

  // Check if a piece is favorited
  const isFavorited = (pieceId) => {
    return favoritePieces.has(pieceId);
  };

  // Check if a capsule is favorited
  const isCapsuleFavorited = (capsuleId) => {
    return favoriteCapsules.has(capsuleId);
  };

  // Check if a wallet is favorited
  const isWalletFavorited = (walletId) => {
    return favoriteWallets.has(walletId);
  };

  // Get all favorite piece IDs
  const getFavoriteIds = () => {
    return [...favoritePieces];
  };

  // Get all favorite capsule IDs
  const getFavoriteCapsuleIds = () => {
    return [...favoriteCapsules];
  };

  // Get all favorite wallet IDs
  const getFavoriteWalletIds = () => {
    return [...favoriteWallets];
  };

  // Refresh favorites from database
  const refreshFavorites = () => {
    loadFavorites();
  };

  return {
    // State
    favoritePieces,
    favoriteCapsules,
    favoriteWallets,
    loading,
    error,
    
    // Actions
    toggleFavorite,
    toggleCapsuleFavorite,
    toggleWalletFavorite,
    
    // Checkers
    isFavorited,
    isCapsuleFavorited,
    isWalletFavorited,
    
    // Getters
    getFavoriteIds,
    getFavoriteCapsuleIds,
    getFavoriteWalletIds,
    
    // Utilities
    refreshFavorites,
    isConnected
  };
};
